import React, { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../api";
import { Modal, Icon, Spinner, useToast } from "./ui";

function fmt(ts) { try { return new Date(ts).toLocaleString(); } catch { return ""; } }

// keep only safe formatting HTML (posts are authored by trusted staff, but strip anything dangerous)
export function cleanHtml(html) {
  if (!html) return "";
  return String(html)
    .replace(/<\/?(script|style|iframe|object|embed|link|meta)[^>]*>/gi, "")
    .replace(/\son\w+\s*=\s*"[^"]*"/gi, "")
    .replace(/\son\w+\s*=\s*'[^']*'/gi, "")
    .replace(/javascript:/gi, "");
}
const plainText = (html) => String(html || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();

function RichEditor({ value, onChange, placeholder }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current && ref.current.innerHTML !== (value || "")) ref.current.innerHTML = value || ""; /* init once */ }, []); // eslint-disable-line
  const exec = (cmd, arg) => {
    try { document.execCommand("styleWithCSS", false, true); } catch { /* ignore */ }
    document.execCommand(cmd, false, arg);
    if (ref.current) { ref.current.focus(); onChange(ref.current.innerHTML); }
  };
  const btn = (label, cmd, arg, title) => (
    <button type="button" className="rte-btn" title={title} onMouseDown={(e) => e.preventDefault()} onClick={() => exec(cmd, arg)}>{label}</button>
  );
  return (
    <div className="rte">
      <div className="rte-tools">
        {btn(<b>B</b>, "bold", null, "Bold")}
        {btn(<i>I</i>, "italic", null, "Italic")}
        {btn(<u>U</u>, "underline", null, "Underline")}
        {btn(<span style={{ background: "#fde68a", padding: "0 4px", borderRadius: 3 }}>H</span>, "hiliteColor", "#fde68a", "Highlight")}
        {btn("• List", "insertUnorderedList", null, "Bulleted list")}
        {btn("1. List", "insertOrderedList", null, "Numbered list")}
        {btn("⨯", "removeFormat", null, "Clear formatting")}
      </div>
      <div ref={ref} className="rte-area inp" contentEditable suppressContentEditableWarning
        data-ph={placeholder || ""} onInput={() => onChange(ref.current.innerHTML)} />
    </div>
  );
}

/* ---------- Create a post (admin / super-admin / supervisor) ---------- */
export function CreatePostModal({ onClose, onPosted }) {
  const toast = useToast();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [image, setImage] = useState(null);   // data URL
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const onFile = (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) return setErr("Please choose an image file.");
    if (f.size > 3 * 1024 * 1024) return setErr("Image is too large (max 3 MB).");
    const r = new FileReader();
    r.onload = () => setImage(r.result);
    r.readAsDataURL(f);
  };

  const submit = async () => {
    setErr("");
    if (!plainText(body) && !image) return setErr("Add a description or an image.");
    setBusy(true);
    try { await api.createPost({ title: title.trim() || null, body, imageData: image }); toast("Posted", "ok"); onPosted && onPosted(); onClose(); }
    catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <Modal wide title="Create a post" sub="Everyone sees this after they sign in" onClose={onClose}
      foot={<><button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={submit} disabled={busy}>{busy ? <span className="spin" /> : "Post"}</button></>}>
      <div className="field"><label>Title (optional)</label>
        <input className="inp" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. New payor portal added" /></div>
      <div className="field"><label>Description</label>
        <RichEditor value={body} onChange={setBody} placeholder="Write your announcement… use the toolbar to format." /></div>
      <div className="field"><label>Image (optional)</label>
        {image
          ? <div className="post-img-edit"><img src={image} alt="" /><button className="btn btn-ghost btn-sm" onClick={() => setImage(null)}>Remove</button></div>
          : <label className="upload-drop"><Icon.upload width="16" height="16" /> Choose an image<input type="file" accept="image/*" hidden onChange={onFile} /></label>}
      </div>
      {err && <div className="err-text">{err}</div>}
    </Modal>
  );
}

/* ---------- View all posts (from the header) ---------- */
export function PostsViewer({ canManage, onClose }) {
  const toast = useToast();
  const [posts, setPosts] = useState(null);
  const [open, setOpen] = useState(null);   // the post being viewed
  const load = useCallback(() => { api.listPosts().then(setPosts).catch(() => setPosts([])); }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    try { await api.deletePost(id); toast("Deleted", "ok"); load(); setOpen(null); } catch (e) { toast(e.message, "err"); }
  };

  return (
    <Modal wide title="Announcements" sub="All posts, newest first" onClose={onClose}
      foot={<button className="btn btn-primary" onClick={onClose}>Close</button>}>
      {posts === null ? <div className="empty"><Spinner /></div>
        : posts.length === 0 ? <p className="muted">No posts yet.</p>
        : <div className="post-rows">
            {posts.map((p) => (
              <button className="post-row" key={p.id} onClick={() => setOpen(p)}>
                <div className="post-row-main">
                  <span className="post-row-title">{p.title || "Announcement"}</span>
                  <span className="post-row-meta">{p.createdByName} · {fmt(p.createdAt)}</span>
                </div>
                {p.imageData && <span className="post-row-img">image</span>}
                <Icon.chevron width="16" height="16" style={{ transform: "rotate(180deg)", opacity: .4 }} />
              </button>
            ))}
          </div>}

      {open && (
        <Modal wide title={open.title || "Announcement"} sub={`${open.createdByName} · ${fmt(open.createdAt)}`} onClose={() => setOpen(null)}
          foot={<>{canManage && <button className="btn btn-danger" onClick={() => del(open.id)}>Delete</button>}
            <div className="spacer" /><button className="btn btn-primary" onClick={() => setOpen(null)}>Close</button></>}>
          {open.imageData && <img className="post-image" src={open.imageData} alt="" />}
          {open.body && <div className="post-body" dangerouslySetInnerHTML={{ __html: cleanHtml(open.body) }} />}
        </Modal>
      )}
    </Modal>
  );
}

/* ---------- After-login popup of unread posts ---------- */
export function UnreadPosts() {
  const [posts, setPosts] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => { api.unreadPosts().then((p) => setPosts(p || [])).catch(() => setPosts([])); }, []);

  if (!posts.length || idx >= posts.length) return null;
  const p = posts[idx];

  const readAndNext = async () => {
    try { await api.markPostRead(p.id); } catch { /* ignore */ }
    setIdx((i) => i + 1);
  };

  return (
    <Modal wide locked title={p.title || "Announcement"} sub={`${p.createdByName} · ${fmt(p.createdAt)}${posts.length > 1 ? `  ·  ${idx + 1} of ${posts.length}` : ""}`}
      onClose={readAndNext}
      foot={<button className="btn btn-primary" onClick={readAndNext}>Yes, I have read</button>}>
      {p.imageData && <img className="post-image" src={p.imageData} alt="" />}
      {p.body && <div className="post-body" dangerouslySetInnerHTML={{ __html: cleanHtml(p.body) }} />}
    </Modal>
  );
}